import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import { Calendar, Clock, CheckCircle, XCircle, Activity } from "lucide-react";
import { SchoolLayout } from "./SchoolLayout";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { DynamicModuleService } from "@/services/APIs";
import { getUserInfo } from "@/lib/storeManager";
import { useQuery } from "@tanstack/react-query";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const ACTIVITY_SCHEMA_NAME = "Activity Management";
const SUBMISSION_SCHEMA_NAME = "Activity Submission";

// Helper function to normalize mobile number
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber || "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

interface Event {
  id: string;
  title: string;
  description: string;
  location: string;
  start_date: string;
  end_date: string;
  thumbnail_url: string;
}

interface Submission {
  id: string;
  status: string;
  score: number | null;
  submitted_at: string | null;
  activitytitle?: string;
  activity?: {
    title: string;
  };
}

const SchoolDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const getLink = useKarunaLink();
  const userInfo = getUserInfo();
  const normalizedContact = userInfo?.phone_number
    ? normalizeMobileNumber(userInfo.phone_number)
    : "";

  if (import.meta.env.DEV) {
    console.log("🔍 Dashboard Debug:", {
      userInfo,
      normalizedContact,
      hasPhoneNumber: !!userInfo?.phone_number,
    });
  }

  // Fetch activities/events - used for both events display and activity mapping
  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ["activities", ACTIVITY_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const activitiesList =
          await DynamicModuleService.getGridColItemsAllPages(
            ACTIVITY_SCHEMA_NAME
          );

        const activitiesWithFullData = await mapWithConcurrency(
          activitiesList,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (activity: any) => {
            const activityId = activity.id ?? activity._id;
            if (!activityId) {
              return activity;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                ACTIVITY_SCHEMA_NAME,
                activityId
              );
              const data = fullData?.data ?? fullData ?? activity;
              return { ...activity, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for activity ${activityId}:`,
                err
              );
              return activity;
            }
          }
        );

        return activitiesWithFullData;
      } catch (error) {
        console.error("Error fetching activities:", error);
        toast({
          title: "Error",
          description: "Failed to load activities",
          variant: "destructive",
        });
        return [];
      }
    },
  });

  // Fetch submissions - using same logic as SchoolSubmissions.tsx
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ["school-submissions", SUBMISSION_SCHEMA_NAME, normalizedContact],
    queryFn: async () => {
      if (!normalizedContact) {
        if (import.meta.env.DEV) {
          console.log("⚠️ No contact available for filtering");
        }
        return [];
      }
      try {
        const allSubmissions =
          await DynamicModuleService.getGridColItemsAllPages(
            SUBMISSION_SCHEMA_NAME
          );

        if (import.meta.env.DEV) {
          console.log("📋 All submissions fetched:", allSubmissions.length);
          console.log("🔍 Filtering with contact:", normalizedContact);
        }

        const submissionsWithFullData = await mapWithConcurrency(
          allSubmissions,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (submission: any) => {
            const submissionId = submission.id ?? submission._id;
            if (!submissionId) {
              return submission;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                SUBMISSION_SCHEMA_NAME,
                submissionId
              );
              const data = fullData?.data ?? fullData ?? submission;
              // Merge full data with grid data
              return { ...submission, ...data };
            } catch (err) {
              console.warn(
                `⚠️ Could not fetch full data for submission ${submissionId}:`,
                err
              );
              return submission;
            }
          }
        );

        // Filter submissions for this school by contact number
        // Use comprehensive filtering logic like SchoolSubmissions.tsx
        const filtered = submissionsWithFullData.filter((submission: any) => {
          let submissionContact = "";

          // Try nested object first (most likely structure)
          if (
            submission.basicInfo &&
            typeof submission.basicInfo === "object"
          ) {
            submissionContact = normalizeMobileNumber(
              submission.basicInfo.schoolcontact ??
                submission.basicInfo.contact ??
                submission.basicInfo.school_contact ??
                ""
            );
          }

          // If not found, try flat fields (check all possible variations)
          if (!submissionContact) {
            submissionContact = normalizeMobileNumber(
              submission.schoolcontact ??
                submission["basicInfo.schoolcontact"] ??
                submission.contact ??
                submission.contact_number ??
                submission.school_contact ??
                submission.schoolContact ??
                submission.school_Contact ??
                ""
            );
          }

          // If still not found, search all keys (case-insensitive)
          if (!submissionContact) {
            const allKeys = Object.keys(submission);
            const contactKey = allKeys.find(
              key =>
                key.toLowerCase().includes("contact") ||
                key.toLowerCase().includes("schoolcontact")
            );
            if (contactKey) {
              submissionContact = normalizeMobileNumber(
                String(submission[contactKey] ?? "")
              );
            }
          }

          // Match by contact number
          if (normalizedContact && submissionContact) {
            return submissionContact === normalizedContact;
          }

          return false;
        });

        console.log("✅ Filtered submissions count:", filtered.length);
        return filtered;
      } catch (error) {
        console.error("❌ Error fetching submissions:", error);
        toast({
          title: "Error",
          description: "Failed to load submissions",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!normalizedContact,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Helper function to calculate activity status based on dates
  const getActivityStatus = (
    activity: any
  ): "ongoing" | "upcoming" | "completed" => {
    const startDateStr = activity.startdate ?? activity.start_date;
    const endDateStr = activity.enddate ?? activity.end_date;

    if (!startDateStr || !endDateStr) {
      return "upcoming";
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const startDate = new Date(startDateStr);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(endDateStr);
    endDate.setHours(0, 0, 0, 0);

    // If end date is in the past, mark as completed
    if (endDate < now) {
      return "completed";
    }

    // If start date is in the future, mark as upcoming
    if (startDate > now) {
      return "upcoming";
    }

    // Otherwise, it's ongoing (between start and end date)
    return "ongoing";
  };

  // Transform activities data
  const events = useMemo((): Event[] => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      console.log("📊 No activities data or not an array:", activitiesData);
      return [];
    }

    console.log("📊 Total activities fetched:", activitiesData.length);

    const filteredAndMapped = activitiesData
      .filter((activity: any) => {
        // Show activities that are ongoing or upcoming (not completed)
        const status = getActivityStatus(activity);
        const shouldShow = status === "ongoing" || status === "upcoming";

        // Also check if there's an explicit status field that might indicate active
        const explicitStatus =
          activity.status?.toLowerCase() ??
          activity.Status?.toLowerCase() ??
          "";
        const isExplicitlyActive =
          explicitStatus === "active" || explicitStatus === "published";

        return shouldShow || isExplicitlyActive;
      })
      .map((activity: any) => {
        const activityId = activity.id ?? activity._id ?? "";
        return {
          id: activityId,
          title:
            activity.activitytitle ??
            activity.title ??
            activity.activity_title ??
            "Untitled Activity",
          description: activity.description ?? "",
          location: activity.location ?? "",
          start_date: activity.start_date ?? activity.startdate ?? "",
          end_date: activity.end_date ?? activity.enddate ?? "",
          thumbnail_url:
            activity.banner_url ??
            activity.thumbnail_url ??
            activity.bannerurl ??
            activity.banner ??
            "",
        };
      })
      .filter(event => {
        // Filter out events without valid IDs
        return event.id && event.title !== "Untitled Activity";
      })
      .sort((a, b) => {
        // Sort by start date
        if (!a.start_date || !b.start_date) {
          return 0;
        }
        return (
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
        );
      })
      .slice(0, 3); // Limit to 3

    console.log("📊 Filtered events:", filteredAndMapped.length);
    return filteredAndMapped;
  }, [activitiesData]);

  // Create a map of activity ID to title (for fallback if event_id exists)
  const activityMap = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    activitiesData.forEach((activity: any) => {
      const activityId = String(activity.id ?? activity._id ?? "").trim();
      const title =
        activity.activitytitle ??
        activity.title ??
        activity.activity_title ??
        "Unknown Activity";
      if (activityId) {
        map.set(activityId, title);
      }
    });
    return map;
  }, [activitiesData]);

  // Transform submissions data - using same approach as SchoolSubmissions.tsx
  const submissions = useMemo((): Submission[] => {
    if (!submissionsData || !Array.isArray(submissionsData)) {
      console.log("📊 No submissions data or not an array:", submissionsData);
      return [];
    }

    console.log("📊 Total submissions fetched:", submissionsData.length);

    const mapped = submissionsData
      .map((submission: any): Submission | null => {
        const eventId = String(
          submission.event_id ??
            submission.eventid ??
            submission.activity_id ??
            submission.activityid ??
            submission.basicInfo?.event_id ??
            submission.basicInfo?.eventid ??
            ""
        ).trim();
        const submissionId = submission.id ?? submission._id ?? "";

        if (!submissionId) {
          return null;
        }

        // Try to get activity title from event_id, otherwise use activitytitle field
        let displayTitle = "Unknown Activity";
        if (eventId) {
          const activityTitle = activityMap.get(eventId);
          if (activityTitle) {
            displayTitle = activityTitle;
          }
        }

        // Fallback to activitytitle field if event_id mapping didn't work
        if (displayTitle === "Unknown Activity") {
          displayTitle =
            submission.activitytitle ??
            submission.activity_title ??
            submission.activity?.title ??
            "Unknown Activity";
        }

        // Get submission date - prefer submitted_at, then created_on, then created_at
        const submittedAt =
          submission.submitted_at ??
          submission.created_on ??
          submission.created_at ??
          submission.submit_date ??
          submission.updated_on ??
          null;

        // Normalize status to lowercase for consistent comparison
        const rawStatus = submission.status ?? submission.Status ?? "pending";
        const normalizedStatus = String(rawStatus).toLowerCase().trim();

        return {
          id: submissionId,
          status: normalizedStatus,
          score: submission.score ?? submission.Score ?? null,
          submitted_at: submittedAt,
          activitytitle: displayTitle,
          activity: {
            title: displayTitle,
          },
        };
      })
      .filter((s): s is Submission => s !== null)
      .sort((a, b) => {
        // Sort by submitted_at descending (most recent first)
        if (!a.submitted_at || !b.submitted_at) {
          return 0;
        }
        return (
          new Date(b.submitted_at).getTime() -
          new Date(a.submitted_at).getTime()
        );
      })
      .slice(0, 5); // Limit to 5 for dashboard

    console.log("📊 Processed submissions:", mapped.length);
    return mapped;
  }, [submissionsData, activityMap]);

  const loading = activitiesLoading || submissionsLoading;

  const getSubmissionStats = () => {
    // Make status comparison case-insensitive
    const stats = {
      total: submissions.length,
      pending: submissions.filter(s => s.status?.toLowerCase() === "pending")
        .length,
      approved: submissions.filter(s => s.status?.toLowerCase() === "approved")
        .length,
      rejected: submissions.filter(s => s.status?.toLowerCase() === "rejected")
        .length,
    };

    console.log("📊 Calculated Stats:", {
      ...stats,
      allStatuses: submissions.map(s => s.status),
      submissionsCount: submissions.length,
    });

    return stats;
  };

  const stats = getSubmissionStats();

  const getStatusBadge = (status: string) => {
    // Normalize status to lowercase for consistent matching
    const normalizedStatus = String(status || "pending")
      .toLowerCase()
      .trim();

    const config: Record<string, { color: string; icon: any; label: string }> =
      {
        pending: {
          color: "bg-yellow-500",
          icon: Clock,
          label: "Pending",
        },
        approved: {
          color: "bg-green-500",
          icon: CheckCircle,
          label: "Approved",
        },
        rejected: {
          color: "bg-red-500",
          icon: XCircle,
          label: "Rejected",
        },
      };

    const {
      color,
      icon: Icon,
      label,
    } = config[normalizedStatus] || config.pending;

    return (
      <Badge
        className={`${color} flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium text-white`}
      >
        <Icon className="h-3.5 w-3.5" />
        {label}
      </Badge>
    );
  };

  return (
    <SchoolLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Welcome Back!</h1>
          <p className="mt-1 text-muted-foreground">
            View your activities and manage your school profile
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <Card className="border-border/50 shadow-medium transition-shadow hover:shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                    Total Submissions
                  </h3>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats.total}
                  </p>
                </div>
                <Activity className="h-6 w-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium transition-shadow hover:shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                    Approved
                  </h3>
                  <p className="text-3xl font-bold text-green-600">
                    {stats.approved}
                  </p>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium transition-shadow hover:shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                    Pending Review
                  </h3>
                  <p className="text-3xl font-bold text-yellow-600">
                    {stats.pending}
                  </p>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-500/20">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium transition-shadow hover:shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                    Active Events
                  </h3>
                  <p className="text-3xl font-bold text-blue-600">
                    {events.length}
                  </p>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-500/20">
                  <Calendar className="h-5 w-5 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Submissions Widget */}
        <Card className="border-border/50 shadow-medium">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Submissions</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(getLink("/school/submissions"))}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center">
                <p className="text-muted-foreground">Loading submissions...</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-muted-foreground">No submissions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map(submission => (
                  <div
                    key={submission.id}
                    className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex-1">
                      <h4 className="text-base font-semibold">
                        {submission.activity?.title ??
                          submission.activitytitle ??
                          "Unknown Activity"}
                      </h4>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Submitted:{" "}
                        {submission.submitted_at
                          ? new Date(
                              submission.submitted_at
                            ).toLocaleDateString("en-US", {
                              month: "numeric",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "N/A"}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      {submission.status?.toLowerCase() === "approved" &&
                        submission.score !== null && (
                          <div className="text-right">
                            <p className="text-base font-bold text-blue-600">
                              {submission.score}/100
                            </p>
                          </div>
                        )}
                      {getStatusBadge(submission.status || "pending")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
};

export default SchoolDashboard;
